import { api, endpoints } from "api/api";
import { AxiosPromise } from "axios";

// Mirrors Pazia.System.Queries.CourseFinances.GetReconciliation.CourseFinanceReconciliationDto (camelCase, enums as numbers).
// Dates: dateConvertInterceptor turns offset-less ISO strings into Date, but strings with "Z" stay strings,
// hence `Date | string`.

export const RECONCILIATION_ISSUE_CODE = {
  PAYMENT_AMOUNT_MISMATCH: 1,
  PAYMENT_WITHOUT_TRANCHE: 2,
  TRANCHE_PAID_WITHOUT_PAYMENT: 3,
  PAYMENT_FOR_UNPAID_TRANCHE: 4,
  DUPLICATE_PAYMENT: 5,
  OVERPAID: 6,
  UNPARSEABLE_AMOUNT: 7,
} as const;

export const RECONCILIATION_ISSUE_SEVERITY = {
  WARNING: 1,
  ERROR: 2,
} as const;

export const RECONCILIATION_PAYMENT_SOURCE = {
  AUTOPAY: 1,
  OUTSIDE_SYSTEM: 2,
  MULTI_COURSE: 3,
} as const;

export const RECONCILIATION_COURSE_WARNING_CODE = {
  REMOVED_STUDENT_WITH_PAYMENTS: 1,
  TRAINER_RATES_WITHOUT_COSTS: 3,
  REMOVED_STUDENT_REFUND_MISMATCH: 4,
} as const;

/** AutopayPaymentStatus */
export const RECONCILIATION_PAYMENT_STATUS = {
  PENDING: 1,
  SUCCESS: 2,
  FAILURE: 3,
  CANCELLED: 4,
  REFUND: 5,
} as const;

/** RegistrationTranchePaymentStatus */
export const RECONCILIATION_TRANCHE_STATUS = {
  NOT_PAID: 1,
  PENDING_PAYMENT: 2,
  PAID_BY_USER: 3,
  SKIPPED_BY_ADMIN: 4,
  PAYED_OUTSIDE_SYSTEM: 5,
} as const;

export interface ReconciliationIssueApi {
  code: number;
  severity: number;
  impact: number | null;
  paymentId: number | null;
  courseTrancheId: number | null;
}

export interface ReconciliationSummaryApi {
  balance: number;
  dueForPaidTranches: number;
  registrationRevenue: number;
  difference: number;
  remainingToPay: number;
  costs: number;
  errorCount: number;
  warningCount: number;
}

export interface ReconciliationTrancheApi {
  courseTrancheId: number;
  name: string | null;
  amount: number;
  discountedAmount: number;
  status: number;
  dueDate: Date | string;
  paymentIds: number[];
}

export interface ReconciliationPaymentApi {
  paymentId: number;
  source: number;
  rawAmount: string;
  /** Parsed amount — filled even for non-SUCCESS rows, never sum it client-side. */
  countedAmount: number;
  status: number;
  createdDate: Date | string;
  paymentDate: Date | string | null;
  courseTrancheIds: number[];
  invoiceId: number | null;
  comment: string | null;
}

export interface ReconciliationStudentApi {
  registrationId: number;
  userId: number;
  firstName: string;
  surname: string;
  discountCode: string | null;
  originalPrice: number;
  price: number;
  dueForPaidTranches: number;
  remaining: number;
  countedRevenue: number;
  difference: number;
  unexplained: number;
  retainedRevenue: number | null;
  tranches: ReconciliationTrancheApi[];
  payments: ReconciliationPaymentApi[];
  issues: ReconciliationIssueApi[];
}

export interface ReconciliationExamPaymentApi {
  paymentId: number;
  registrationId: number;
  studentName: string;
  examName: string | null;
  countedAmount: number;
  status: number;
  createdDate: Date | string;
}

export interface ReconciliationRefundApi {
  refundPaymentId: number;
  originalPaymentId: number;
  registrationId: number;
  studentName: string;
  rawAmount: string;
  status: number;
  /** RefundStatus (1..5), null for legacy refund rows. */
  refundStatus: number | null;
  createdDate: Date | string;
}

export interface ReconciliationTrainerCostApi {
  entryId: number;
  trainerName: string;
  scopeLabel: string;
  value: number;
  date: Date | string;
}

export interface ReconciliationManualEntryApi {
  id: number;
  name: string;
  category: string;
  value: number;
  date: Date | string;
}

export interface ReconciliationCourseWarningApi {
  code: number;
  registrationId: number | null;
  paymentId: number | null;
  amount: number | null;
}

export interface CourseFinanceReconciliationApi {
  summary: ReconciliationSummaryApi;
  students: ReconciliationStudentApi[];
  removedStudents: ReconciliationStudentApi[];
  examPayments: ReconciliationExamPaymentApi[];
  refunds: ReconciliationRefundApi[];
  trainerCosts: ReconciliationTrainerCostApi[];
  manualEntries: ReconciliationManualEntryApi[];
  courseWarnings: ReconciliationCourseWarningApi[];
}

const fetchReconciliation = (courseId: number | string): AxiosPromise<CourseFinanceReconciliationApi> => {
  return api.get<CourseFinanceReconciliationApi>(endpoints.COURSE_FINANCES.GET_RECONCILIATION(courseId));
};

export const courseFinanceReconciliationApi = { fetchReconciliation };
